import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCw, Activity, Clock, Video } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/videos/health`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setLoading(false); }
  };

  const runCheck = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/videos/health-check`);
      const r = res.data;
      if (r.broken_count === 0) {
        toast.success(`${r.ok_count}/${r.total_videos} videos OK ✅`);
      } else {
        toast.error(`${r.broken_count} videos rotos detectados`);
      }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setRunning(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('es-ES') : '';
  const fmtDur = (ms) => ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div></AdminLayout>;
  }

  const lr = data?.last_run;
  const broken = data?.current_broken || [];
  const history = data?.history || [];
  const allOk = lr && lr.broken_count === 0 && lr.total_videos > 0;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="video-health-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Activity className="h-6 w-6 text-primary" /> Salud de Videos
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Verifica que todos los videos están físicamente presentes y son reproducibles. Se ejecuta automáticamente cada 24h.
            </p>
          </div>
          <Button onClick={runCheck} disabled={running} data-testid="run-check-btn">
            {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Verificar ahora
          </Button>
        </div>

        {/* Estado general */}
        {!lr ? (
          <Card className="bg-card border-dashed">
            <CardContent className="py-10 text-center text-muted-foreground text-sm">
              Aún no se ha ejecutado ningún chequeo. Pulsa "Verificar ahora".
            </CardContent>
          </Card>
        ) : (
          <Card className={`bg-card border-2 ${allOk ? 'border-green-500/40' : 'border-red-500/40'}`}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3 flex-wrap">
                {allOk ? (
                  <>
                    <CheckCircle2 className="h-12 w-12 text-green-400" />
                    <div className="flex-1">
                      <p className="text-2xl font-bold text-green-400">Todo OK</p>
                      <p className="text-sm text-muted-foreground">
                        {lr.ok_count}/{lr.total_videos} videos están presentes y reproducibles
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-12 w-12 text-red-400" />
                    <div className="flex-1">
                      <p className="text-2xl font-bold text-red-400">{lr.broken_count} videos con problemas</p>
                      <p className="text-sm text-muted-foreground">
                        {lr.ok_count}/{lr.total_videos} OK · Resto requiere atención
                      </p>
                    </div>
                  </>
                )}
                <div className="text-xs text-muted-foreground text-right">
                  <Clock className="h-3 w-3 inline mr-1" />
                  Último chequeo: {fmtDate(lr.started_at)}<br />
                  Duración: {fmtDur(lr.duration_ms || 0)}<br />
                  Ejecutado por: {lr.triggered_by}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de rotos */}
        {broken.length > 0 && (
          <Card className="bg-card border-red-500/30">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-400" /> Videos rotos ({broken.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Estos ejercicios tienen <code>video_url</code> en la base de datos pero el archivo no existe o no se puede leer. Resube el video desde la biblioteca.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {broken.map((b, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 p-3 bg-zinc-900/50 rounded">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground text-sm truncate">{b.exercise_name}</p>
                      <p className="text-xs text-muted-foreground truncate">URL: {b.video_url}</p>
                    </div>
                    <Badge variant="outline" className="border-red-500/40 text-red-400 text-xs">{b.reason}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Histórico */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-base">Histórico ({history.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Sin histórico</p>
            ) : (
              <div className="space-y-1">
                {history.map((h, i) => {
                  const ok = (h.broken_count || 0) === 0 && (h.total_videos || 0) > 0;
                  return (
                    <div key={i} className="flex items-center justify-between gap-2 p-2 text-sm border-b border-zinc-800 last:border-0">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {ok ? <CheckCircle2 className="h-4 w-4 text-green-400 flex-shrink-0" />
                             : <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />}
                        <Video className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="tabular-nums text-foreground">{h.ok_count}/{h.total_videos} OK</span>
                        {h.broken_count > 0 && <Badge variant="outline" className="border-red-500/40 text-red-400 text-xs">-{h.broken_count}</Badge>}
                        <span className="text-xs text-muted-foreground truncate">· {h.triggered_by}</span>
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {fmtDate(h.started_at)} · {fmtDur(h.duration_ms || 0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default VideoHealthPage;
